// src/pages/permissions/DepartmentForm.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Box,
  Grid,
  Typography,
  MenuItem,
  FormHelperText,
  Select,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardContent,
  Checkbox,
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import LayoutContainer from '../../components/LayoutContainer';
import AdminHeader from '../../components/AdminHeader';
import departmentService from '../../services/departmentService';
import { filter, find, includes, isEmpty, map, toUpper } from 'lodash';
import eventService from '../../services/eventService';
import itemTypeService from '../../services/itemTypeService';

const DepartmentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [initialValues, setInitialValues] = useState({
    name: '',
    contact_name: null,
    contact_number: null,
    active: true,
    station_type: '',
    item_type_id: [],
  });

  const fetchProductTypes = async (type, obj) => {
    const response = await itemTypeService.getItemTypes();
    if (obj) {
      const data = {
        ...obj,
        item_type_id:
          map(
            filter(filter(response?.data, { station_type: type }), (data) =>
              includes(JSON.parse(obj?.item_type_id), data?.id)
            ),
            'type'
          ) || [],
      };
      setInitialValues(data);
    }
    setProductTypes(filter(response?.data, { station_type: type }));
  };
  
  useEffect(() => {
    const fetchDepartment = async () => {
      if (id) {
        const response = await departmentService.getDepartmentById(id);
        if (response?.status === 200) {
          fetchProductTypes(response?.data?.station_type, response?.data);
        }
      }
    };
    fetchDepartment();
  }, [id]);

  // useEffect(() => {
  //   const fetchEvents = async () => {
  //     const response = await eventService.getEvents();
  //     setEvents(response.data);
  //   };
  //   fetchEvents();
  // }, []);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Station is required'),
    station_type: Yup.string().required('Station type is required'),
    item_type_id: Yup.array()
      .required('Product type is required')
      .min(1, 'At least one product type is required'),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    const data = {
      ...values,
      item_type_id: JSON.stringify(
        map(
          values?.item_type_id,
          (data) => find(productTypes, { type: data })?.id
        )
      ),
    };
    try {
      if (id) {
        await departmentService.updateDepartment(id, data);
      } else {
        await departmentService.createDepartment(data);
      }
      navigate('/departments');
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = {
    color: '#000',
    fontSize: '0.87rem',
    marginTop: 8,
  };

  return (
    <LayoutContainer>
      <AdminHeader
        title={id ? 'Edit Station' : 'Add Station'}
        backTo='/departments'
      />
      <Box
        className='page-content-wrapper scrollable'
        p={3}
        sx={{ display: 'flex', backgroundColor: '#efefef' }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            handleChange,
            handleBlur,
            handleSubmit,
            values,
            errors,
            touched,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit} style={{ width: '50%' }}>
              <Card>
                <CardContent>
                  <Grid container spacing={3} sx={{ p: 1 }}>
                    <Grid item xs={12}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Station Name <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <TextField
                        placeholder='Enter Station Name'
                        name='name'
                        value={values.name}
                        fullWidth
                        onChange={(e) => {
                          const capitalizedValue = toUpper(e.target.value); // Capitalize first letter
                          handleChange({
                            target: { name: 'name', value: capitalizedValue },
                          });
                        }}
                        onBlur={handleBlur}
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          // width : '50%',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.name && errors.name
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                      />
                      {touched.name && errors.name && (
                        <FormHelperText error>{errors.name}</FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12} sx={{ pt: '10px !important' }}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Contact Name
                      </Typography>
                      <TextField
                        placeholder='Enter Contact Name'
                        name='contact_name'
                        fullWidth
                        value={values.contact_name}
                        onChange={(e) => {
                          const capitalizedValue = toUpper(e.target.value); // Capitalize first letter
                          handleChange({
                            target: {
                              name: 'contact_name',
                              value: capitalizedValue,
                            },
                          });
                        }}
                        onBlur={handleBlur}
                        error={
                          touched.contact_name && Boolean(errors.contact_name)
                        }
                        helperText={touched.contact_name && errors.contact_name}
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          // width : '50%',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.contact_name && errors.contact_name
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sx={{ pt: '10px !important' }}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Contact Number
                      </Typography>
                      <TextField
                        placeholder='Enter Contact Number'
                        name='contact_number'
                        fullWidth
                        value={values.contact_number}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.contact_number &&
                          Boolean(errors.contact_number)
                        }
                        helperText={
                          touched.contact_number && errors.contact_number
                        }
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          // width : '50%',
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.contact_number && errors.contact_number
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sx={{ pt: '10px !important' }}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Type <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <RadioGroup
                        name='station_type'
                        // fullWidth
                        value={values.station_type || ''}
                        onChange={(e) => {
                          const capitalizedValue = toUpper(e.target.value); // Capitalize first letter
                          handleChange({
                            target: {
                              name: 'station_type',
                              value: capitalizedValue,
                            },
                          });

                          fetchProductTypes(capitalizedValue);
                        }}
                        onBlur={handleBlur}
                        row
                        sx={{ gap: 3 }}
                      >
                        <FormControlLabel
                          value='JUNCTION'
                          control={<Radio />}
                          label='Junction'
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              fontSize: '0.675rem',
                              fontWeight: 500,
                              color: '#333',
                            },
                          }}
                        />
                        <FormControlLabel
                          value='ROUTE'
                          control={<Radio />}
                          label='Route'
                          sx={{
                            '& .MuiFormControlLabel-label': {
                              fontSize: '0.675rem',
                              fontWeight: 500,
                              color: '#333',
                            },
                          }}
                        />
                      </RadioGroup>
                      {touched.station_type && errors.station_type && (
                        <FormHelperText error>
                          {errors.station_type}
                        </FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12} sx={{ pt: '10px !important' }}>
                      <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        Product Types
                      </Typography>
                      <TextField
                        select
                        name='item_type_id'
                        fullWidth
                        value={values?.item_type_id || []}
                        onChange={(e) => {
                          const {
                            target: { value },
                          } = e;
                          setFieldValue(
                            'item_type_id',
                            typeof value === 'string' ? value.split(',') : value
                          );
                          handleChange(e);
                        }}
                        onBlur={handleBlur}
                        sx={{
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor:
                                touched.item_type_id && errors.item_type_id
                                  ? '#d32f2f'
                                  : '#ccc',
                            },
                          },
                        }}
                        SelectProps={{
                          multiple: true,
                          renderValue: (selected) => selected?.join(', '), // Display selected roles as a comma-separated string
                          // MenuProps: {
                          //   ...menuBox,
                          // },
                        }}
                      >
                        {productTypes?.map((type) => (
                          <MenuItem key={type.type} value={type.type}>
                            <Checkbox
                              checked={values.item_type_id?.includes(type.type)}
                              sx={{ padding: 0, marginRight: '8px' }}
                            />
                            {toUpper(type.type)}
                          </MenuItem>
                        ))}
                      </TextField>
                      {touched.item_type_id && errors.item_type_id && (
                        <FormHelperText error>
                          {errors.item_type_id}
                        </FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12} sx={{ pt: '10px !important' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginTop: 1,
                        }}
                      >
                        <Button
                          type='submit'
                          variant='contained'
                          sx={{
                            backgroundColor: '#be210b',
                            color: '#fff',
                            textTransform: 'none',
                            padding: '5px 15px',
                            '&:hover': {
                              backgroundColor: '#a2180a',
                            },
                          }}
                        >
                          {id ? 'Update' : 'Create'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </form>

            // <form onSubmit={handleSubmit}>
            //   <Grid container spacing={3}>
            //     <Grid item xs={12}>
            //       <Typography variant='body2' sx={{ fontWeight: 600 }}>
            //         Station Name
            //       </Typography>
            //       <TextField
            //         placeholder='Enter Station Name'
            //         name='name'
            //         fullWidth
            //         value={values.name}
            //         onChange={handleChange}
            //         onBlur={handleBlur}
            //         sx={{
            //           borderRadius: 1,
            //           '& .MuiOutlinedInput-root': {
            //             '& fieldset': {
            //               borderColor:
            //                 touched.name && errors.name ? '#d32f2f' : '#ccc',
            //             },
            //           },
            //         }}
            //       />
            //       {touched.name && errors.name && (
            //         <FormHelperText error>{errors.name}</FormHelperText>
            //       )}
            //     </Grid>

            //     <Grid item xs={12}>
            //       <Typography variant='body2' sx={{ fontWeight: 600 }}>
            //         Contact Name
            //       </Typography>
            //       <TextField
            //         placeholder='Enter Contact Name'
            //         name='contact_name'
            //         fullWidth
            //         value={values.contact_name}
            //         onChange={handleChange}
            //         onBlur={handleBlur}
            //         error={touched.contact_name && Boolean(errors.contact_name)}
            //         helperText={touched.contact_name && errors.contact_name}
            //         sx={{
            //           borderRadius: 1,
            //           '& .MuiOutlinedInput-root': {
            //             '& fieldset': {
            //               borderColor:
            //                 touched.contact_name && errors.contact_name
            //                   ? '#d32f2f'
            //                   : '#ccc',
            //             },
            //           },
            //         }}
            //       />
            //     </Grid>

            //     <Grid item xs={12}>
            //       <Typography variant='body2' sx={{ fontWeight: 600 }}>
            //         Contact Number
            //       </Typography>
            //       <TextField
            //         placeholder='Enter Contact Number'
            //         name='contact_number'
            //         fullWidth
            //         value={values.contact_number}
            //         onChange={handleChange}
            //         onBlur={handleBlur}
            //         error={
            //           touched.contact_number && Boolean(errors.contact_number)
            //         }
            //         helperText={touched.contact_number && errors.contact_number}
            //         sx={{
            //           borderRadius: 1,
            //           '& .MuiOutlinedInput-root': {
            //             '& fieldset': {
            //               borderColor:
            //                 touched.contact_number && errors.contact_number
            //                   ? '#d32f2f'
            //                   : '#ccc',
            //             },
            //           },
            //         }}
            //       />
            //     </Grid>

            //     <Grid item xs={12}>
            //       <Typography variant='body2' sx={{ fontWeight: 600 }}>
            //         Department Type
            //       </Typography>
            //       <RadioGroup
            //         name='dept_type'
            //         value={values.dept_type || ''}
            //         onChange={handleChange}
            //         onBlur={handleBlur}
            //         row
            //         sx={{ gap: 3 }}
            //       >
            //         <FormControlLabel
            //           value='JUNCTION'
            //           control={<Radio />}
            //           label='Junction'
            //           sx={{
            //             '& .MuiFormControlLabel-label': {
            //               fontSize: '0.875rem',
            //               fontWeight: 500,
            //               color: '#333',
            //             },
            //           }}
            //         />
            //         <FormControlLabel
            //           value='ROUTE'
            //           control={<Radio />}
            //           label='Route'
            //           sx={{
            //             '& .MuiFormControlLabel-label': {
            //               fontSize: '0.875rem',
            //               fontWeight: 500,
            //               color: '#333',
            //             },
            //           }}
            //         />
            //       </RadioGroup>
            //       {touched.dept_type && errors. && (
            //         <FormHelperText error>{errors.dept_type}</FormHelperText>
            //       )}
            //     </Grid>

            //     <Grid item xs={12}>
            //       <Box
            //         sx={{
            //           display: 'flex',
            //           justifyContent: 'flex-end',
            //           marginTop: 2,
            //         }}
            //       >
            //         <Button
            //           type='submit'
            //           variant='contained'
            //           sx={{
            //             backgroundColor: '#be210b',
            //             color: '#fff',
            //             textTransform: 'none',
            //             padding: '10px 20px',
            //             '&:hover': {
            //               backgroundColor: '#a2180a',
            //             },
            //           }}
            //         >
            //           {id ? 'Update' : 'Create'}
            //         </Button>
            //       </Box>
            //     </Grid>
            //   </Grid>
            // </form>
          )}
        </Formik>
      </Box>
    </LayoutContainer>
  );
};

export default DepartmentForm;
